import { Add, Close, InfoOutlined } from "@mui/icons-material";
import {
    Box,
    Grid,
    IconButton,
    Link,
    Tooltip,
    styled,
    useTheme,
} from "@mui/material";
import { FilterLabel } from "components/Conversations/Views/Drawer/shared";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import type { ModuleScore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import {
    type ApplicationFilterModuleScoreRestrictionValue,
    type IApplicationFilterComponent,
} from "../BaseFilterComponents/IApplicationFilterComponent";
import { Operator } from "models/Rule";
import {
    describeConditionConflict,
    findConflictingCondition,
    getAddConditionState,
    getModuleScoreBlockInterval,
    groupModuleScores,
    MODULE_SCORE_MAX as MAX_MODULE_SCORE,
    MODULE_SCORE_MIN as MIN_MODULE_SCORE,
    operatorOptions,
    type ModuleScoreConditionRow as ConditionRow,
    type ModuleScoreGroup,
} from "./moduleScoreLabels";

export { operatorOptions };

const FullWidthGrid = styled(Grid)(() => ({ width: "100%" }));
/** Lets a row reveal its own remove control on hover without each row holding hover state. */
const REMOVE_CONDITION_CLASS = "acx-module-score-remove";

type ModulesScoresFilterProps = IApplicationFilterComponent;

type ScoreBounds = {
    min: number;
    max: number;
};

function toTwoDecimalScore(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

function clampScore(
    value: number | undefined,
    min = MIN_MODULE_SCORE,
    max = MAX_MODULE_SCORE,
) {
    const nextValue =
        typeof value === "number" && Number.isFinite(value)
            ? value
            : MIN_MODULE_SCORE;
    const normalizedMin = Math.max(MIN_MODULE_SCORE, Math.min(MAX_MODULE_SCORE, min));
    const normalizedMax = Math.max(
        normalizedMin,
        Math.min(MAX_MODULE_SCORE, max),
    );

    return toTwoDecimalScore(
        Math.min(normalizedMax, Math.max(normalizedMin, nextValue)),
    );
}

function resolveScoreBounds(
    restriction?: ApplicationFilterModuleScoreRestrictionValue,
): ScoreBounds {
    const min =
        typeof restriction?.min === "number" && Number.isFinite(restriction.min)
            ? clampScore(restriction.min)
            : MIN_MODULE_SCORE;
    const max =
        typeof restriction?.max === "number" && Number.isFinite(restriction.max)
            ? clampScore(restriction.max, min)
            : MAX_MODULE_SCORE;
    return { min, max };
}

function areModuleScoresEqual(left: ModuleScore[] = [], right: ModuleScore[] = []) {
    if (left.length !== right.length) return false;
    return left.every((entry, index) => {
        const other = right[index];
        return (
            entry.licensedModuleId === other?.licensedModuleId &&
            entry.operator === other?.operator &&
            entry.moduleScore === other?.moduleScore
        );
    });
}

export const ModuleScoresFilter = observer(function (
    props: ModulesScoresFilterProps,
) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    if (!authStore.canUserView("Module Scores")) {
        return null;
    }
    if (
        authStore.canUserView("Restrict Evaluations") &&
        !authStore.isUserUltra()
    ) {
        return null;
    }

    const moduleScores = props.store.moduleScores ?? [];
    const [isAddingResult, setIsAddingResult] = useState(
        moduleScores.length === 0,
    );
    // Keyed by position in the flat filter array, because a module no longer identifies a row.
    // Structural edits clear the buffers rather than reindexing them: the values are transient
    // typing state, and re-reading them from the filter is always correct.
    const [scoreInputs, setScoreInputs] = useState<Record<number, string>>({});
    // Value problems and conflicts are held apart because they belong to different things. A value
    // problem is the row's own, while a conflict is a property of the pair, so editing either row
    // has to rewrite both rows' conflicts without disturbing what either says about its own value.
    const [valueErrors, setValueErrors] = useState<Record<number, string>>({});
    const [conflictErrors, setConflictErrors] = useState<Record<number, string>>(
        {},
    );
    const moduleScoresSignature = moduleScores
        .map(
            (entry) =>
                `${entry.licensedModuleId}::${entry.operator ?? ""}::${entry.moduleScore ?? ""}`,
        )
        .join("||");

    const restrictedModuleIds = useMemo(
        () =>
            (props.optionRestriction?.values ?? [])
                .map((value) => `${value}`.trim())
                .filter((value) => value.length > 0),
        [props.optionRestriction?.values],
    );
    const restrictedModuleIdSet = useMemo(
        () => new Set(restrictedModuleIds),
        [restrictedModuleIds],
    );
    const isAllowOnlyRestriction = props.optionRestriction?.type === "allowOnly";
    const hasModuleOptionRestrictions = restrictedModuleIds.length > 0;
    const moduleScoreRestrictionsById = useMemo(
        () => props.moduleScoreRestriction?.byModuleId ?? {},
        [props.moduleScoreRestriction?.byModuleId],
    );

    const isModuleAllowed = useCallback(
        (moduleId: string) => {
            if (!hasModuleOptionRestrictions) return true;
            return isAllowOnlyRestriction
                ? restrictedModuleIdSet.has(moduleId)
                : !restrictedModuleIdSet.has(moduleId);
        },
        [
            hasModuleOptionRestrictions,
            isAllowOnlyRestriction,
            restrictedModuleIdSet,
        ],
    );

    const getModuleBounds = useCallback(
        (moduleId: string): ScoreBounds => {
            return resolveScoreBounds(moduleScoreRestrictionsById[moduleId]);
        },
        [moduleScoreRestrictionsById],
    );

    /**
     * The dashboard's own conditions on a module.
     *
     * A chart naming the module starts from these and may only tighten them. They lead the block,
     * so a condition's position within its group is what says whether it is one of them.
     */
    const getInheritedConditions = useCallback(
        (moduleId: string): ModuleScore[] =>
            moduleScoreRestrictionsById[moduleId]?.conditions ?? [],
        [moduleScoreRestrictionsById],
    );

    const hasDashboardRangeRestriction = useCallback(
        (moduleId: string): boolean => {
            const { min, max } = getModuleBounds(moduleId);
            return min > MIN_MODULE_SCORE || max < MAX_MODULE_SCORE;
        },
        [getModuleBounds],
    );

    useEffect(() => {
        const currentModuleScores = props.store.moduleScores ?? [];
        if (currentModuleScores.length === 0) return;

        // Values follow a restriction that tightens; comparisons are left alone. Rewriting them
        // would push the dashboard's operator onto rows the user added, which are meant to be
        // free, and a block whose shape no longer matches its dashboard is the clamp's to replace
        // rather than this effect's to quietly redraw.
        const sanitizedModuleScores = currentModuleScores
            .filter((entry) => isModuleAllowed(entry.licensedModuleId))
            .map((entry) => {
                const scoreBounds = getModuleBounds(entry.licensedModuleId);

                return {
                    ...entry,
                    moduleScore: clampScore(
                        entry.moduleScore,
                        scoreBounds.min,
                        scoreBounds.max,
                    ),
                };
            });

        if (!areModuleScoresEqual(currentModuleScores, sanitizedModuleScores)) {
            props.store.setModuleScores(sanitizedModuleScores);
        }
    }, [
        getModuleBounds,
        isModuleAllowed,
        moduleScoresSignature,
        moduleScoreRestrictionsById,
        props.store,
    ]);

    useEffect(() => {
        props.store.setHasError(
            [
                ...Object.values(valueErrors),
                ...Object.values(conflictErrors),
            ].some((error) => error.length > 0),
        );
    }, [props.store, valueErrors, conflictErrors]);

    useEffect(() => {
        setIsAddingResult(props.store.moduleScores?.length === 0);
    }, [props.store.moduleScores?.length]);

    // Removing the filter takes its errors with it. Without this the drawer would close over an
    // error nothing on screen still shows, and Apply would stay disabled with no way to fix it.
    const store = props.store;
    useEffect(() => () => store.setHasError(false), [store]);

    const selectableModules = useMemo(() => {
        const allModules = valuesStore.hierarchyLicensedModules ?? [];
        if (!hasModuleOptionRestrictions) return allModules;
        return allModules.filter((module) => isModuleAllowed(module.id));
    }, [hasModuleOptionRestrictions, isModuleAllowed, valuesStore.hierarchyLicensedModules]);

    const groups = groupModuleScores(moduleScores);
    // Explained once at the title rather than per row, so the icon is where the Classifiers filter
    // puts its own and a block is not peppered with the same marker.
    const hasInheritedConditions = groups.some(
        (group) => getInheritedConditions(group.licensedModuleId).length > 0,
    );

    const addableModuleOptions = (() => {
        const selectedIds = new Set(groups.map((group) => group.licensedModuleId));
        return selectableModules.filter((module) => !selectedIds.has(module.id));
    })();

    // A module owns a whole block, so every other block's module is taken.
    const getSelectableModulesForGroup = (currentModuleId: string) => {
        const selectedIds = new Set(
            groups
                .map((group) => group.licensedModuleId)
                .filter((moduleId) => moduleId !== currentModuleId),
        );

        return selectableModules.filter((module) => !selectedIds.has(module.id));
    };

    const clearScoreBuffers = () => {
        setScoreInputs({});
        setValueErrors({});
        setConflictErrors({});
    };

    /**
     * Rewrites the conflict message on every row of a module, given the edit about to be applied.
     *
     * A conflict belongs to a pair, not to a row, so the row the user is editing is never the only
     * one whose message changes. Changing `>= 70` to `<= 70` clears the complaint sitting on the
     * `< 40` beside it, and it is that row the user is looking at.
     *
     * The block is read as the edit would leave it rather than as it currently stands, because the
     * filter has not been written yet when this runs.
     */
    const revalidateBlockConflicts = (
        group: ModuleScoreGroup,
        index: number,
        next: Partial<ModuleScore>,
    ) => {
        const conditions = group.conditions.map((row) =>
            row.index === index ? { ...row.entry, ...next } : row.entry,
        );
        const messages: Record<number, string> = {};

        group.conditions.forEach((row, position) => {
            const conflict = findConflictingCondition(conditions, position);
            messages[row.index] = conflict
                ? describeConditionConflict(conditions[position], conflict)
                : "";
        });

        setConflictErrors((prev) => ({ ...prev, ...messages }));
    };

    /**
     * The conditions a module's block opens with.
     *
     * A restricted module inherits the dashboard's conditions, so a chart starts from the range
     * already in force and can only tighten it. An unrestricted one opens with a single empty
     * condition, which is what adding a module has always done.
     */
    const buildBlockForModule = (moduleId: string): ModuleScore[] => {
        const inherited = getInheritedConditions(moduleId);
        if (inherited.length > 0) {
            return inherited.map((condition) => ({
                licensedModuleId: moduleId,
                operator: condition.operator,
                moduleScore: condition.moduleScore,
            }));
        }

        return [
            {
                licensedModuleId: moduleId,
                operator: undefined,
                moduleScore: getModuleBounds(moduleId).min,
            },
        ];
    };

    /**
     * The value a row should hold the moment its comparison is chosen.
     *
     * A lower bound opens at the floor of what the module's other conditions already allow and an
     * upper bound at their ceiling, so the new row starts out saying nothing and the block keeps
     * the population it had. Narrowing from there is the user's to do.
     */
    const openingScoreFor = (
        group: ModuleScoreGroup,
        operator: Operator | undefined,
    ): number => {
        const bounds = getModuleBounds(group.licensedModuleId);
        const blockInterval = getModuleScoreBlockInterval(
            group.conditions.map((row) => row.entry),
        );
        const opensAt =
            operator === Operator.Lt || operator === Operator.LtEq
                ? blockInterval.max
                : blockInterval.min;

        return clampScore(opensAt, bounds.min, bounds.max);
    };

    const replaceEntry = (index: number, next: Partial<ModuleScore>) => {
        props.store.setModuleScores(
            moduleScores.map((entry, entryIndex) =>
                entryIndex === index ? { ...entry, ...next } : entry,
            ),
        );
    };

    /** Inserts after the group's last condition so a module's conditions stay contiguous. */
    const addConditionToGroup = (group: ModuleScoreGroup) => {
        if (!getAddConditionState(group.conditions.map((row) => row.entry)).canAdd) {
            return;
        }

        const insertAt =
            group.conditions[group.conditions.length - 1].index + 1;
        const next = [...moduleScores];

        // The value waits for the comparison. Until one is chosen there is no direction to pick a
        // sensible number for, and guessing one only to have the user choose the other direction
        // is what left a fresh row reading as a conflict.
        next.splice(insertAt, 0, {
            licensedModuleId: group.licensedModuleId,
            operator: undefined,
            moduleScore: getModuleBounds(group.licensedModuleId).min,
        });

        props.store.setModuleScores(next);
        clearScoreBuffers();
    };

    /**
     * Removing the first condition removes the module, the way the module select sitting on that
     * row reads. Removing any other drops only itself.
     */
    const removeCondition = (group: ModuleScoreGroup, condition: ConditionRow) => {
        const isFirstCondition = group.conditions[0].index === condition.index;
        const removedIndexes = isFirstCondition
            ? new Set(group.conditions.map((row) => row.index))
            : new Set([condition.index]);
        const remaining = moduleScores.filter(
            (_, index) => !removedIndexes.has(index),
        );

        props.store.setModuleScores(remaining);
        clearScoreBuffers();
        if (remaining.length === 0) setIsAddingResult(true);
    };

    /** The module select belongs to the block, so changing it moves every condition in it. */
    const changeGroupModule = (
        group: ModuleScoreGroup,
        nextModuleId: string,
    ) => {
        const firstIndex = group.conditions[0].index;
        const groupIndexes = new Set(group.conditions.map((row) => row.index));
        const next: ModuleScore[] = [];

        moduleScores.forEach((entry, index) => {
            if (index === firstIndex) {
                next.push(...buildBlockForModule(nextModuleId));
                return;
            }
            if (groupIndexes.has(index)) return;
            next.push(entry);
        });

        props.store.setModuleScores(next);
        clearScoreBuffers();
    };

    const theme = useTheme();

    const renderModuleSelect = (group: ModuleScoreGroup) => (
        <AcxSelectSingle
            id={`module-score-select-${group.licensedModuleId}`}
            defaultValue={valuesStore.hierarchyLicensedModules?.find(
                (x) => x.id === group.licensedModuleId,
            )}
            options={getSelectableModulesForGroup(group.licensedModuleId)}
            labelField="title"
            valueField="id"
            fullWidth
            onChange={(item) => {
                if (!item?.id) return;
                changeGroupModule(group, item.id);
            }}
            placeholder="Select Module..."
            isLoading={valuesStore.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_LICENSED_HIERARCHY_MODULES,
            )}
        />
    );

    // Says a conversation has to satisfy both conditions. Without it a block is two rows that look
    // independent, and the reader has to guess. It sits between the rows it joins.
    const renderAndMarker = () => (
        <Box
            sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                "& > span": {
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: theme.palette.gray[600],
                    backgroundColor: theme.palette.gray[200],
                    borderRadius: 1,
                    padding: "2px 8px",
                },
            })}
        >
            <span>AND</span>
        </Box>
    );

    const renderAddCondition = (group: ModuleScoreGroup) => {
        const { canAdd, reason } = getAddConditionState(
            group.conditions.map((row) => row.entry),
        );

        return (
            <Grid
                container
                item
                alignItems="center"
                columnGap={1}
                sx={{ paddingTop: "2px" }}
            >
                <Grid item>
                    <Link
                        component="button"
                        type="button"
                        underline="none"
                        disabled={!canAdd}
                        onClick={() => addConditionToGroup(group)}
                        sx={(theme) => ({
                            fontSize: "14px",
                            fontWeight: 600,
                            color: theme.palette.gray[500],
                            cursor: canAdd ? "pointer" : "not-allowed",
                            "&:hover": {
                                color: canAdd
                                    ? theme.palette.primary.main
                                    : theme.palette.gray[400],
                            },
                            "&:disabled": {
                                color: theme.palette.gray[300],
                            },
                        })}
                    >
                        + Condition
                    </Link>
                </Grid>
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                    <Tooltip title={reason} placement="top">
                        <InfoOutlined
                            sx={(theme) => ({
                                fontSize: "16px",
                                color: theme.palette.primary.main,
                                cursor: "help",
                            })}
                        />
                    </Tooltip>
                </Grid>
            </Grid>
        );
    };

    const renderCondition = (
        group: ModuleScoreGroup,
        condition: ConditionRow,
        positionInGroup: number,
    ) => {
        const { entry, index } = condition;
        const isFirstCondition = positionInGroup === 0;
        const scoreBounds = getModuleBounds(group.licensedModuleId);
        const inheritedConditions = getInheritedConditions(
            group.licensedModuleId,
        );
        // Inherited conditions lead the block, so a row's position says whether it is one.
        const inheritedCondition = inheritedConditions[positionInGroup];
        const isInherited = positionInGroup < inheritedConditions.length;
        // An inherited row holds one end of the dashboard's range. Its comparison is fixed and its
        // value moves only inside that range, so the block cannot reach back out. A row the user
        // added is free in both, because ANDing it can only narrow what the inherited rows bound.
        const isOperatorLocked =
            isInherited && inheritedCondition?.operator !== undefined;
        const rowOperatorOptions = isOperatorLocked
            ? operatorOptions.filter(
                  (option) => option.value === inheritedCondition?.operator,
              )
            : operatorOptions;
        const selectedOperator = operatorOptions.find(
            (option) =>
                option.value ===
                (isOperatorLocked ? inheritedCondition?.operator : entry.operator),
        );
        // Removing an inherited row past the first would reopen that end of the dashboard's range.
        // The first row still removes the module, which only ever excludes more.
        const canRemoveCondition = isFirstCondition || !isInherited;
        // A value problem outranks a conflict, because a number the row cannot parse has to be
        // fixed before the pair means anything.
        const rowError =
            valueErrors[index] || conflictErrors[index] || "";

        return (
            // The selects read their value from defaultValue, so a row has to remount when the
            // module behind it changes for a restriction's locked operator to be picked up. The
            // index disambiguates the several conditions a module now carries.
            <FullWidthGrid
                key={`${index}::${entry.licensedModuleId}`}
                container
                item
                columnGap={1}
                flexWrap="nowrap"
                alignItems="center"
                sx={{
                    [`&:hover .${REMOVE_CONDITION_CLASS}`]: { opacity: 1 },
                }}
            >
                <Grid item sx={{ flex: "0 0 45%", minWidth: 0 }}>
                    <AcxSelectSingle
                        id={`module-score-operator-${index}`}
                        options={rowOperatorOptions}
                        defaultValue={selectedOperator}
                        labelField="label"
                        valueField="value"
                        fullWidth
                        isDisabled={isOperatorLocked}
                        onChange={(item) => {
                            if (isOperatorLocked) return;
                            const operator = item?.value;
                            // A row with no comparison yet has no meaningful value either, so the
                            // value is chosen here, where the direction is finally known. Opening
                            // a lower bound at the block's floor and an upper bound at its ceiling
                            // leaves the block unchanged until the user narrows it.
                            const next: Partial<ModuleScore> =
                                entry.operator === undefined
                                    ? {
                                          operator,
                                          moduleScore: openingScoreFor(
                                              group,
                                              operator,
                                          ),
                                      }
                                    : { operator };

                            replaceEntry(index, next);
                            setScoreInputs((prev) => {
                                const nextInputs = { ...prev };
                                delete nextInputs[index];
                                return nextInputs;
                            });
                            revalidateBlockConflicts(group, index, next);
                        }}
                        placeholder="Condition"
                        isClearable={!isOperatorLocked}
                    />
                </Grid>
                <Grid item sx={{ flex: "1 1 0", minWidth: 0 }}>
                    <AcxMainTextField
                        type="text"
                        textContainerSx={{ margin: "0" }}
                        placeholderText="Score"
                        id={`moduleScore-${index}`}
                        value={
                            scoreInputs[index] ?? String(entry.moduleScore ?? "")
                        }
                        error={rowError.length > 0}
                        errorHelper={rowError || "Error"}
                        onChange={(item) => {
                            const value = item.target.value;
                            const isEmpty = value === "";
                            const isNumeric = !isNaN(Number(value));
                            const numericValue = Number(value);
                            const matchesPattern = /^\d{0,3}(\.\d{0,2})?$/.test(
                                value,
                            );
                            const isWithinDefaultRange =
                                !isEmpty &&
                                isNumeric &&
                                numericValue <= MAX_MODULE_SCORE &&
                                numericValue >= MIN_MODULE_SCORE;
                            const isWithinRestrictedRange =
                                !isEmpty &&
                                isNumeric &&
                                numericValue <= scoreBounds.max &&
                                numericValue >= scoreBounds.min;
                            const valid =
                                matchesPattern &&
                                (isEmpty ||
                                    (isWithinDefaultRange &&
                                        isWithinRestrictedRange));

                            let error = "";

                            if (!isEmpty && !isNumeric) {
                                error = "Value must be a number.";
                            } else if (!isEmpty && !matchesPattern) {
                                error =
                                    "Value must be up to 3 digits and optionally 2 decimal places.";
                            } else if (
                                !isEmpty &&
                                !isWithinRestrictedRange &&
                                hasDashboardRangeRestriction(
                                    group.licensedModuleId,
                                )
                            ) {
                                error = `Value must be between ${scoreBounds.min} and ${scoreBounds.max} (set by dashboard filters).`;
                            } else if (!isEmpty && !isWithinDefaultRange) {
                                error = "Value must be between 0 and 100.";
                            } else if (!isEmpty && !isWithinRestrictedRange) {
                                error = `Value must be between ${scoreBounds.min} and ${scoreBounds.max}.`;
                            }

                            setScoreInputs((prev) => ({
                                ...prev,
                                [index]: value,
                            }));
                            setValueErrors((prev) => ({
                                ...prev,
                                [index]: valid ? "" : error,
                            }));
                            // A value the row cannot even parse says nothing about the pair, so the
                            // block is re-read as it stands and this row's own message covers it.
                            revalidateBlockConflicts(
                                group,
                                index,
                                valid && !isEmpty
                                    ? { moduleScore: numericValue }
                                    : {},
                            );

                            if (valid && value !== "") {
                                replaceEntry(index, {
                                    moduleScore: clampScore(
                                        parseFloat(value),
                                    ),
                                });
                            } else if (value === "") {
                                replaceEntry(index, {
                                    moduleScore: hasDashboardRangeRestriction(
                                        group.licensedModuleId,
                                    )
                                        ? scoreBounds.min
                                        : MIN_MODULE_SCORE,
                                });
                            }
                        }}
                    />
                </Grid>
                <Grid item sx={{ width: "28px", flex: "none" }}>
                    {canRemoveCondition && (
                    <IconButton
                        className={REMOVE_CONDITION_CLASS}
                        size="small"
                        title={
                            isFirstCondition
                                ? "Remove module"
                                : "Remove condition"
                        }
                        aria-label={
                            isFirstCondition
                                ? "Remove module"
                                : "Remove condition"
                        }
                        onClick={() => removeCondition(group, condition)}
                        sx={{
                            opacity: 0,
                            transition: "opacity 120ms",
                            // Keyboard users never trigger the row hover, so focus reveals it too.
                            "&:focus-visible": { opacity: 1 },
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                    )}
                </Grid>
            </FullWidthGrid>
        );
    };

    return (
        <Grid id={props.id} container item direction="column" rowGap={1}>
            <Grid
                container
                item
                width="100%"
                flexWrap="nowrap"
                justifyContent="space-between"
                alignItems="center"
                columnGap={1}
            >
                {/* The title sits on the same row as the control that adds a module, the way the
                    Classifiers filter arranges its own. ApplicationFilterItem leaves the label to
                    this component for that reason, and keeps the filter's own remove control where
                    it always was. */}
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                    <FilterLabel sx={{ mb: 0 }}>Module Scores</FilterLabel>
                    {hasInheritedConditions && (
                        <Tooltip
                            title="Some conditions come from the dashboard filter. Their comparisons are fixed and their values can only narrow the range the dashboard set."
                            arrow
                        >
                            <InfoOutlined
                                color="primary"
                                sx={{
                                    height: "16px",
                                    width: "16px",
                                    marginLeft: "4px",
                                }}
                            />
                        </Tooltip>
                    )}
                </Grid>
                <Grid item sx={{ marginLeft: "auto" }}>
                    <IconButton
                        title="Add module score"
                        aria-label="Add module score"
                        onClick={() => setIsAddingResult(true)}
                        disabled={addableModuleOptions.length === 0}
                        sx={(theme) => ({
                            width: "32px",
                            height: "32px",
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            color: theme.palette.gray[600],
                        })}
                    >
                        <Add fontSize="small" />
                    </IconButton>
                </Grid>
            </Grid>
            {groups.map((group) => (
                <Grid
                    key={group.licensedModuleId}
                    sx={{
                        flexWrap: "nowrap",
                        backgroundColor: theme.palette.lightgray.background,
                        padding: 1,
                        borderRadius: 1,
                    }}
                    container
                    item
                    direction="column"
                    rowGap={1}
                >
                    <FullWidthGrid item>
                        {renderModuleSelect(group)}
                    </FullWidthGrid>
                    {group.conditions.map((condition, conditionIndex) => (
                        <React.Fragment key={condition.index}>
                            {conditionIndex > 0 && renderAndMarker()}
                            {renderCondition(group, condition, conditionIndex)}
                        </React.Fragment>
                    ))}
                    {renderAddCondition(group)}
                </Grid>
            ))}
            {isAddingResult && (
                <Grid
                    sx={{
                        gap: 1,
                        flexWrap: "nowrap",
                        backgroundColor: theme.palette.lightgray.background,
                        padding: 1,
                        borderRadius: 1,
                    }}
                    container
                    item
                    direction="row"
                    alignItems="flex-start"
                >
                    <FullWidthGrid
                        container
                        item
                        direction="column"
                        gap={1}
                        flexWrap="nowrap"
                    >
                        <AcxSelectSingle
                            id={"module-score-select"}
                            fullWidth
                            options={addableModuleOptions}
                            valueField="id"
                            labelField="title"
                            onChange={(item) => {
                                if (!item?.id) return;

                                setIsAddingResult(false);
                                props.store.setModuleScores([
                                    ...moduleScores,
                                    ...buildBlockForModule(item.id),
                                ]);
                            }}
                            placeholder="Select Module..."
                            isLoading={valuesStore.getTaskLoading(
                                ApplicationFilterValuesStore.Tasks
                                    .LOAD_LICENSED_HIERARCHY_MODULES,
                            )}
                        />
                        <FullWidthGrid
                            container
                            item
                            columnGap={1}
                            flexWrap="nowrap"
                            alignItems="center"
                        >
                            <Grid item xs={5}>
                                <AcxSelectSingle
                                    id={"module-score-operator"}
                                    fullWidth
                                    isDisabled
                                    options={[]}
                                    labelField={""}
                                    valueField={""}
                                    placeholder="Condition"
                                />
                            </Grid>
                            <Grid item xs={7}>
                                <AcxMainTextField
                                    type="number"
                                    textContainerSx={{ margin: "0" }}
                                    placeholderText="Score"
                                    id="moduleScore"
                                    isDisabled
                                />
                            </Grid>
                        </FullWidthGrid>
                    </FullWidthGrid>
                    {moduleScores.length > 0 && (
                        <Grid item>
                            <IconButton
                                size="small"
                                title="Cancel"
                                aria-label="Cancel adding a module score"
                                onClick={() => {
                                    setIsAddingResult(false);
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );
});
